import React, { useState } from "react";
import * as Mui from "@mui/material";
import { CloudDownload, Description, DownloadForOffline } from "@mui/icons-material";
import * as XLSX from "xlsx";
import { BulkBankImportData } from "./Bulk_bank_details";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { Loader } from "components/Loader";
import { BackArrow } from "components/CustomComponents/BackArrow";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { themes } from "services/constants";

export const BulkBankImportPage = () => {
  const history = useHistory();
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const [importedData, setImportData] = useState([]);
  const [bulkData, setBulkData] = useState([]);
  const [fileName, setFileName] = useState("");
  const [fileSize, setFileSize] = useState("");
  const [page, setPage] = useState(0);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [showListView, setShowListView] = useState(false);
  const [loading, setLoading] = useState(false);

  const header = [
    "Emp ID",
    "Account Holder Name",
    "Bank Name",
    "Account No",
    "IFSC",
    "Branch",
  ];

  const downloadSampleFile = () => {
    const sampleData = [
      {
        "Emp ID": "959953",
        "Account holder name": "Organflow",
        "Bank Name": "CANARA BANK",
        "Account No": "1208111114886",
        "IFSC Code": "CNRB0001208",
        Branch: "Erode",
      }
    ];

    const ws = XLSX.utils.json_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sample");
    XLSX.writeFile(wb, "Sample Bank details.xlsx");
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFileName(file.name);
    setFileSize((file.size / 1024).toFixed(2) + " KB");
    setLoading(true);

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const bstr = event.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];

        const data = XLSX.utils.sheet_to_json(ws);

        const response = await fetch(`${domain}bulk-bank-import/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
          body: JSON.stringify(data),
        });

        const res = await response.json();

        if (response.ok) {
          // FIX: assign a stable unique rowid to every row so the edit drawer
          // can correctly identify which row is being edited. Without this,
          // rowid is always undefined, reset() never fires on a new row open,
          // and editing one record's field overwrites all rows.
          const dataWithStatus = res.map((row, index) => ({
            ...row,
            rowid: row.rowid ?? row.id ?? `row-${index}`,
            status: row.is_valid ? "Valid" : "Invalid",
          }));

          setImportData(dataWithStatus);
          setBulkData(dataWithStatus);

          const hasErrors = dataWithStatus.some(
            (row) => row.is_valid === false
          );
          setButtonDisabled(hasErrors);
          setShowListView(true);
        } else {
          showToast("error", "Data validation failed. Please check the file and try again.");
        }
      } catch (error) {
        console.error('[BulkBankImport] operation failed:', error);
        showToast("error", "Error reading or processing the file.");
      } finally {
        setLoading(false);
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = null;
  };

  const clearImportedData = () => {
    setImportData([]);
    setBulkData([]);
    setFileName("");
    setFileSize("");
    setShowListView(false);
  };

  return (
    <React.Fragment>
      {!loading && !showListView ? (
        <Mui.Grid container sx={{ paddingTop: 3 }}>
          <Mui.Grid xs={2.5}>
            <BackArrow
              actionFunction={() => {
                history.push("/admin/hrzone/employees");
              }}
            />
          </Mui.Grid>
          <Mui.Grid
            xs={7}
            sx={{
              paddingTop: "8%",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Mui.Box component={Mui.Paper} elevation={3}>
              <Mui.Typography
                sx={{ fontWeight: "bold", fontSize: 22, padding: 2 }}
              >
                Bulk Bank Import
              </Mui.Typography>
              <Mui.Grid
                container
                sx={{ paddingLeft: 3, paddingRight: 3, paddingTop: 2 }}
              >
                <Mui.Grid
                  container
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: 5,
                    border: `1px solid ${themes.blueColor}`,
                    borderStyle: "dashed",
                  }}
                >
                  <Mui.Stack>
                    <Mui.Grid
                      container
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {/* Changed CloudUpload to CloudDownload to match the downward arrow from index.js */}
                      <CloudDownload
                        sx={{ color: themes.blueColor }}
                        fontSize="large"
                      />
                    </Mui.Grid>
                    <Mui.Typography sx={{ textAlign: "center", fontSize: 20 }}>
                      Browse your file(s){" "}
                      <Mui.Button component="label">
                        <Mui.Typography
                          sx={{
                            fontSize: 17,
                            textTransform: "capitalize",
                          }}
                        >
                          Import
                        </Mui.Typography>
                        <input
                          accept=".xlsx, .xls, .csv"
                          style={{ display: "none" }}
                          type="file"
                          onChange={handleFileUpload}
                        />
                      </Mui.Button>{" "}
                    </Mui.Typography>
                    <Mui.Typography
                      sx={{ fontSize: 20, color: themes.colorShadow }}
                    >
                      Max 10 MB files are allowed
                    </Mui.Typography>
                  </Mui.Stack>
                </Mui.Grid>
                <Mui.Grid
                  container
                  sx={{
                    paddingTop: 3,
                  }}
                >
                  <Mui.Typography
                    sx={{ fontSize: 15, color: themes.colorShadow }}
                  >
                    Only support .xlsx, .xls, .csv file
                  </Mui.Typography>
                </Mui.Grid>
              </Mui.Grid>
              <Mui.Divider sx={{ paddingLeft: 3, paddingRight: 3 }}>
                Sample File
              </Mui.Divider>
              <Mui.Grid
                container
                sx={{ paddingLeft: 3, paddingRight: 3, paddingBottom: 2 }}
              >
                <Mui.Grid
                  container
                  sx={{
                    borderRadius: 2,
                    border: `0.5px solid ${themes.shadowText}`,
                    borderColor: themes.shadowText,
                  }}
                >
                  <Mui.Grid xs={1.5} sx={{ paddingTop: 1.5, paddingLeft: 1 }}>
                    <Description
                      fontSize="large"
                      sx={{ color: themes.primaryIndicator, fontSize: 45 }}
                    />
                  </Mui.Grid>
                  <Mui.Grid xs={3.5} sx={{ paddingTop: 1 }}>
                    <Mui.Stack>
                      <Mui.Typography>Sample Bank details.xlsx</Mui.Typography>
                      <Mui.Typography>288 B</Mui.Typography>
                    </Mui.Stack>
                  </Mui.Grid>
                  <Mui.Grid
                    xs={7}
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "flex-end",
                      alignItems: "flex-end",
                    }}
                  >
                    <Mui.IconButton onClick={() => downloadSampleFile()}>
                      <DownloadForOffline
                        size="large"
                        sx={{ color: themes.downloadIconColor, fontSize: 50 }}
                      />
                    </Mui.IconButton>
                  </Mui.Grid>
                </Mui.Grid>
              </Mui.Grid>
            </Mui.Box>
          </Mui.Grid>
          <Mui.Grid xs={2.5}></Mui.Grid>
        </Mui.Grid>
      ) : loading ? (
        <Loader info="Reading and Validating File..." />
      ) : null}
      {showListView ? (
        <Mui.Grid container sx={{ paddingTop: 3 }}>
          <BulkBankImportData
            header={header}
            importedData={importedData}
            setImportData={setImportData}
            page={page}
            setPage={setPage}
            fileName={fileName}
            clearImportedData={clearImportedData}
            fileSize={fileSize}
            setBulkData={setBulkData}
            bulkData={bulkData}
            buttonDisabled={buttonDisabled}
            setButtonDisabled={setButtonDisabled}
            setShowListView={setShowListView}
          />
        </Mui.Grid>
      ) : null}
    </React.Fragment>
  );
};